import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Icon, Popover, Text } from '@amzn/storm-ui';
import { LinkProps } from '@amzn/aac-chrome-internal-api';
import { externalLink, questionCircleOutline } from '@amzn/storm-ui-icons';
import { selectHelpMenu } from 'src/features/header/store/slice';
import { RootState } from 'src/app/store';
import { HelpDropdownPublisher } from 'src/commons/utils/metrics';
import { useAccountDropdownContext } from '../AccountDropdown/AccountDropdownProvider';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider/InternalChromeConfigContextProvider';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';
import UtilityButton from 'src/features/header/components/UtilityMenu/UtilityButton';
import { StyledLink } from 'src/commons/components/StyledLink';

// TODO: Remove aac-help-menu-item-feature-center usage after the back-end cleanup
// https://issues.amazon.com/issues/P209748810
const featureCenterUid = 'aac-help-menu-item-feature-center';
const supportCenterUid = 'aac-help-menu-item-support-center';
const adsStatusUid = 'aac-help-menu-item-ads-status';
const helpMenuDropdownId = 'aac-support-center-dropdown';

const StyledHelpMenuLink = styled(StyledLink)`
  &&&& {
    display: block;
    padding: ${({ theme }) => theme.spacing.xsmall.default};

    &:hover {
      background-color: ${({ theme }) =>
        theme.selectableContainer.backgroundColor.hover};
    }
  }
`;

const HelpMenuLink: React.VFC<{ helpLinks: LinkProps[] }> = ({ helpLinks }) => {
  const { hasAccessToSA, hasAccessToDSP } = useAccountDropdownContext();
  return (
    <>
      {helpLinks.map(({ label, url, isExternal, uid }) => (
        <StyledHelpMenuLink
          key={label}
          href={
            uid === supportCenterUid
              ? `${url}${
                  url.includes('?') ? '&' : '?'
                }hasAccessToSA=${hasAccessToSA}&hasAccessToDSP=${hasAccessToDSP}`
              : url
          }
          target="_blank"
          rel="noreferrer"
          {...getTaktAttributes(
            `help-dropdown-link${uid ? '-' + uid : ''}`,
            chromeComponents.HEADER,
            {
              url,
              ...(!!uid && { uid }),
            },
          )}
        >
          <Text>
            {label} {isExternal === true && <Icon type={externalLink} />}
          </Text>
        </StyledHelpMenuLink>
      ))}
    </>
  );
};

interface HelpDropdownProps {
  helpInfo: Array<LinkProps>;
}

const HelpDropdown: React.VFC<HelpDropdownProps> = ({ helpInfo }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { config: internalChromeConfig } = useInternalChromeConfigContext();

  const handleToggle = () => {
    if (isOpen) {
      HelpDropdownPublisher.publishCounterMonitor('Close', 1);
    } else {
      HelpDropdownPublisher.publishCounterMonitor('Open', 1);
    }
    setIsOpen(!isOpen);
  };
  const handleClose = useCallback(() => setIsOpen(false), [setIsOpen]);

  const helpLinks = helpInfo.filter((helpLink) => {
    if (internalChromeConfig.retailerId) {
      return helpLink.uid !== featureCenterUid && helpLink.uid !== adsStatusUid;
    }
    return helpLink.uid !== featureCenterUid;
  });

  return (
    <Popover
      isOpen={isOpen}
      autoFocus={true}
      trigger={
        <UtilityButton
          icon={questionCircleOutline}
          onClick={handleToggle}
          label={helpInfo?.[0]?.label ?? ''}
          aria-haspopup={true}
          aria-expanded={isOpen}
          data-ccx-e2e-id={helpMenuDropdownId}
          {...getTaktAttributes(helpMenuDropdownId, chromeComponents.HEADER)}
        />
      }
      position="bottom"
      align="center"
      strategy="fixed"
      onCloseButtonClick={handleClose}
      spacing={0}
    >
      <div data-ccx-e2e-id="support-center">
        <HelpMenuLink helpLinks={helpLinks} />
      </div>
    </Popover>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    helpInfo: selectHelpMenu(state),
  };
};

export default connect(mapStateToProps)(HelpDropdown);
